import React, { useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import { useSelector } from 'react-redux';
import MaterialTable from 'material-table';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { Query, useApolloClient } from 'react-apollo';
import moment from 'moment';
import { UPCOMING_MEETINGS } from 'graphql/admin/users';
import { CONF_HISTORY } from 'graphql/conference';
import { toUserTimezone } from 'utils';
import { USER_TYPES } from 'constant/userTypes';
import uniqBy from 'lodash/uniqBy';
import humanDuration from 'humanize-duration';
import FilterListIcon from '@material-ui/icons/FilterList';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

const initialFilterValue = moment()
    .add(3, 'd')
    .endOf('day')
    .toJSON();

const AdminHome = ({ history }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [end, setend] = useState(initialFilterValue);
  const [EmptyMsgValue, setEmptyMsgValue] = useState(72);
  const user = useSelector((s) => s.user.user);
  const client = useApolloClient();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'filterMenu',
  });

  const handleFilter = (field) => {
    setEmptyMsgValue(field * 24)
    const time = moment()
      .add(field, 'd')
      .endOf('day')
      .toJSON();

    setend(time);
    popupState.close();
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = () => {
    setLoading(true);
    API.get('api', '/twilio/rooms').then((resp) => {
      setRooms(resp);
      setLoading(false);
    });
  };

  const columns = [
    {
      title: 'Room type',
      field: 'type'
    },
    {
      title: 'Student/Group Name',
      field: 'user_first_name',
      render: (row) => row.type === 'group' ? row.name : `${row.user_first_name} ${row.user_last_name}`,
    },
    {
      title: 'Duration',
      field: 'duration',
      render: (row) => {
        const created = moment(row.dateCreated);
        return Math.floor(moment().diff(created, 'minute')) + ' minutes';
      },
    },
    {
      title: 'Scheduled at',
      field: 'due_date',
      render: (row) => {
        const due_date = toUserTimezone(row.dateCreated, user.timezone).format(
          'LLL',
        );
        return due_date;
      },
    },
    {
      title: 'Participants',
      field: 'participants',
      render: (row) => {
        if (row.participants) {
          return row.participants
            .map((i) => `${i.user_first_name} ${i.user_last_name}`)
            .join(', ');
        }
        return null;
      },
    },
  ];

  const start = moment().toJSON();
  

  return (
    <div>
      <Typography variant={'h4'} gutterBottom>
        Welcome {user.fname}!
      </Typography>
      <MaterialTable
        title="Calls in progress"
        isLoading={loading}
        options={{
          search: false,
          paging: false,
        }}
        localization={{
          body: {
            emptyDataSourceMessage: 'No call in progress',
          },
        }}
        columns={columns}
        data={rooms}
        actions={[
          {
            icon: 'visibility',
            tooltip: 'View Student Profile',
            onClick: (event, rowData) =>
              history.push(`/students/${rowData.user_id}/profile`),
          },
          {
            icon: 'refresh',
            tooltip: 'Refresh Data',
            isFreeAction: true,
            onClick: fetchRooms,
          },
        ]}
      />
      <Box marginTop={3}>
        <Query
          query={UPCOMING_MEETINGS}
          fetchPolicy={'cache-and-network'}
          variables={{ start, end, coach: {} }}
        >
          {({ data, loading }) => {
            const columns = [
              {
                title: 'Student name',
                render: (row) => `${row.user.name} ${row.user.user_last_name}`,
              },
              { title: 'Meeting', field: 'exercise.name' },
              {
                title: 'Scheduled at',
                field: 'due_date',
                render: (row) =>
                  toUserTimezone(row.due_date, user.timezone).format('LLL'),
              },
            ];

            return (
              <MaterialTable
                title="Upcoming Sessions"
                isLoading={loading}
                columns={columns}
                data={data?.rows}
                options={{
                  search: false,
                  paging: false,
                }}
                actions={[
                  {
                    icon: 'visibility',
                    tooltip: 'View Student Profile',
                    onClick: (event, rowData) =>
                      history.push(`/students/${rowData.user_id}/profile`),
                  },
                  {
                    icon: () => (
                      <>
                        <IconButton size="small" {...bindTrigger(popupState)}>
                          {' '}
                          <FilterListIcon />{' '}
                        </IconButton>
                        <Menu {...bindMenu(popupState)}>
                          <MenuItem onClick={() => handleFilter(3)}>
                            72 hours
                          </MenuItem>
                          <MenuItem onClick={() => handleFilter(2)}>
                            48 hours
                          </MenuItem>
                          <MenuItem onClick={() => handleFilter(1)}>
                            24 hours
                          </MenuItem>
                        </Menu>
                      </>
                    ),
                    tooltip: 'Filter',
                    isFreeAction: true,
                    onClick: () => null,
                  },
                ]}
                localization={{
                  body: {
                    emptyDataSourceMessage:
                      `Nothing scheduled in next ${EmptyMsgValue} hours`,
                  },
                }}
              />
            );
          }}
        </Query>
      </Box>
      <Box marginTop={3}>
        <MaterialTable
          title="Past Sessions"
          columns={[
            {
              title: 'ID',
              field: 'id',
              sorting: false,
            },
            {
              title: 'Date',
              field: 'created_at',
              defaultSort: 'desc',
              render: (row) =>
                toUserTimezone(row.created_at, user.timezone).format('LLL'),
            },
            {
              title: 'Duration',
              field: 'duration',
              render: (row) =>
                humanDuration(row.duration * 1000, {
                  units: ['m'],
                  round: true,
                }),
            },
            {
              title: 'Participants',
              sorting: false,
              render: (data) => {
                return (
                  <Box>
                    {uniqBy(
                      data.conference_participants.map((u) => u.user),
                      'user_id',
                    ).map((i) => (
                      <Box>
                        <Typography variant="body2">
                          {i.name} (
                          {USER_TYPES[i.user_type]})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              },
            },
          ]}
          data={(query) => {
            return client
              .query({
                query: CONF_HISTORY,
                variables: {
                  limit: query.pageSize,
                  offset: query.page * query.pageSize,
                  order: [{ [query.orderBy.field]: query.orderDirection }],
                },
              })
              .then((resp) => {
                return {
                  data: resp.data.rows,
                  totalCount: resp.data.count.aggregate.count,
                  page: query.page,
                };
              });
          }}
          options={{
            search: false,
            pageSize: 10,
          }}
          actions={[
            {
              icon: 'play_circle_outline',
              tooltip: 'View Recording',
              onClick: (event, rowData) =>
                history.push(`meeting/${rowData.id}/view`),
            },
          ]}
        />
      </Box>
    </div>
  );
};
export default AdminHome;
